import { Box, Button, Checkbox, Divider, FormControlLabel, InputLabel, TextField, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import styles from './BasicInfo.module.scss';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { IJob, IUpdateJobPayload } from '~/utils/interface/job';
import { BaseRow, BaseSelect, SelectStatus } from '~/components/common';
import SelectAssignee from '~/components/common/select-assignee/SelectAssignee';
import { cast, handleApiError, notify } from '~/utils/helper';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { getIn, useFormik } from 'formik';
import { handleGetJob, handleUpdateJob } from '~/thunks/job/jobThunk';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import {
  MASTER_DATA_ACCESS_CODE,
  MASTER_DATA_ALL_CUSTOMERS,
  MASTER_DATA_BILLING,
  MASTER_DATA_CUSTOMERS,
  MASTER_DATA_CUSTOMER_STATUSES,
  MASTER_DATA_CUSTOMER_TYPE,
  MASTER_DATA_HAZARD,
  MASTER_DATA_JOB_CATEGORIES,
  MASTER_DATA_JOB_PROPERTIES,
  MASTER_DATA_JOB_STATUSES,
  MASTER_DATA_JOB_TYPES,
  MASTER_DATA_PRICES,
  MASTER_DATA_REFERRAL_SOURCES,
  MASTER_DATA_SOURCE_OF_LOSSES,
  MASTER_DATA_WATER_CATEGORY,
  MASTER_DATA_WATER_CLASS,
} from '~/utils/constants/master';
import { DIALOG_MODE_EDIT, VALUE_TRUE } from '~/utils/constants/common';
import { NumericFormat } from 'react-number-format';
import EditJobDialog from '~/components/jobs/edit-job-dialog/EditJobDialog';
import { jobActions } from '~/thunks/job/jobSlice';
import LinkAddress from '~/components/link-address/LinkAddress';
import { ICustomer, ISelectCustomer } from '~/utils/interface/customer';
import { CUSTOMER_TYPES_CONTACT, CUSTOMER_TYPES_ORGANIZATION } from '~/utils/constants/customers';
import { handleCreateCustomer, handleGetDetail } from '~/thunks/customer/customerThunk';
import EditCustomerDialog from '~/components/customer/edit-customer-dialog/EditCustomerDialog';
import { customerActions } from '~/thunks/customer/customerSlice';

const cx = classNames.bind(styles);

type JobListProps = {
  data: IJob;

  updateData: () => void;
};
const initialValues: IUpdateJobPayload = {
  job: {
    hazard: [],
  },
};
const option = [
  { id: 'true', name: 'Yes' },
  {
    id: 'false',
    name: 'No',
  },
];
const BasicInfo = (props: JobListProps) => {
  const { t } = useTranslation('admin/job');
  const { data } = props;
  const [isEditing, setIsEditing] = useState(false); // Toggle for edit mode
  const permissions = usePermission();
  const [openEditJobDialog, setOpenEditJobDialog] = useState<boolean>(false);
  const [openEditCustomerDialog, setOpenEditCustomerDialog] = useState<boolean>(false);
  const { selectAccounts } = useAppSelector((state) => state.account);
  const [editData, setEditData] = useState<IJob>();
  const [editDataCustomer, setEditDataCustomer] = useState<ICustomer>();
  const [editJobDialogMode, setEditJobDialogMode] = useState<string>('');
  const [editCustomerDialogMode, setEditCustomerDialogMode] = useState<string>('');
  const [allCustomerGroups, setAllCustomerGroups] = useState<Array<ISelectCustomer>>();
  const { jobTypes } = useAppSelector((state) => state.jobType);
  const { allCustomers } = useAppSelector((state) => state.master);

  const { billing, accessCode, waterCategory, waterClass, hazard, referralSources } = useAppSelector(
    (state) => state.master
  );
  const dispatch = useAppDispatch();
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      try {
        await dispatch(
          handleUpdateJob({
            id: Number(data?.id),
            params: { ...values, jobAssigneeIds: values?.jobAssigneeIds?.length ? values.jobAssigneeIds : '' },
          })
        ).unwrap();
        notify('success', t('editSuccess', { ns: 'admin/common' }));
        setIsEditing(false);
        if (props.updateData) props.updateData();
      } catch (e: unknown) {
        handleApiError(e, setFieldError);
      }
    },
  });

  useEffect(() => {
    dispatch(
      handleGetMasterData({
        type: [
          MASTER_DATA_JOB_STATUSES,
          MASTER_DATA_BILLING,
          MASTER_DATA_WATER_CATEGORY,
          MASTER_DATA_WATER_CLASS,
          MASTER_DATA_HAZARD,
          MASTER_DATA_ACCESS_CODE,
          MASTER_DATA_REFERRAL_SOURCES,
          MASTER_DATA_ALL_CUSTOMERS,
        ],
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (form.values.contactInsurance) {
      form.setFieldValue('jobInsurance.deductible', 'false');
    } else {
      form.setFieldValue('jobInsurance.deductible', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.contactInsurance]);

  useEffect(() => {
    form.setFieldValue('job.status', cast(data?.status));
    form.setFieldValue('job.statusId', cast(data?.statusId));
    form.setFieldValue('jobAssigneeIds', cast(data?.assigneeIds));
    form.setFieldValue('job.billing', cast(data?.billing));
    form.setFieldValue('job.jobTypeId', cast(data?.jobTypeId));
    form.setFieldValue('job.isAfterHours', data?.isAfterHours);

    //
    form.setFieldValue('job.waterCategory', cast(data?.waterCategory));
    form.setFieldValue('job.waterClass', cast(data?.waterClass));
    form.setFieldValue('job.hazard', cast(data?.hazard));
    form.setFieldValue('job.hazardDocumentation', cast(data?.hazardDocumentation));
    form.setFieldValue('job.accessCode', cast(data?.accessCode));
    form.setFieldValue('job.code', cast(data?.code));
    form.setFieldValue('job.referrer', cast(data?.referrer));
    form.setFieldValue('job.referrerSource', cast(data?.referrerSource));

    form.setFieldValue('contactInsurance', cast(data?.contactInsurance));
    form.setFieldValue('jobInsurance.companyName', cast(data?.jobInsurance?.companyName));
    form.setFieldValue('jobInsurance.policy', cast(data?.jobInsurance?.policy));
    form.setFieldValue('jobInsurance.claim', cast(data?.jobInsurance?.claim));

    form.setFieldValue('jobInsurance.deductible', cast(data?.jobInsurance?.deductible));
    form.setFieldValue('jobInsurance.collectedAmount', cast(data?.jobInsurance?.collectedAmount));
    form.setFieldValue('jobInsurance.other', cast(data?.jobInsurance?.other));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isEditing]);
  useEffect(() => {
    if (allCustomers?.length) {
      const organizations: Array<ISelectCustomer> = [
        {
          id: CUSTOMER_TYPES_ORGANIZATION,
          name: t('editJobDialog.customer.organization'),
          isGroup: true,
        },
      ];

      const contacts: Array<ISelectCustomer> = [
        {
          id: CUSTOMER_TYPES_CONTACT,
          name: t('editJobDialog.customer.contact'),
          isGroup: true,
        },
      ];

      for (let customer of allCustomers) {
        if (customer.type === CUSTOMER_TYPES_ORGANIZATION) {
          organizations.push({
            id: customer?.id,
            name: customer?.name,
          });
        }
        if (customer.type === CUSTOMER_TYPES_CONTACT) {
          contacts.push({
            id: customer?.id,
            name: customer?.name,
          });
        }
      }
      setAllCustomerGroups(organizations.concat(contacts));
    }
  }, [allCustomers, t]);
  const handleEdit = () => {
    setIsEditing(true);
  };
  const fetchMasterData = async () => {
    try {
      await dispatch(
        handleGetMasterData({
          type: [
            MASTER_DATA_JOB_TYPES,
            MASTER_DATA_JOB_CATEGORIES,
            MASTER_DATA_CUSTOMERS,
            MASTER_DATA_CUSTOMER_TYPE,
            MASTER_DATA_PRICES,
            MASTER_DATA_ALL_CUSTOMERS,
            MASTER_DATA_REFERRAL_SOURCES,
            MASTER_DATA_JOB_PROPERTIES,
            MASTER_DATA_CUSTOMER_STATUSES,
            MASTER_DATA_SOURCE_OF_LOSSES,
            MASTER_DATA_WATER_CATEGORY,
            MASTER_DATA_WATER_CLASS,
            MASTER_DATA_HAZARD,
            MASTER_DATA_ACCESS_CODE,
          ],
        })
      ).unwrap();
    } catch (e) {
      handleApiError(e);
    }
  };
  const handleOpenEdit = async () => {
    try {
      await fetchMasterData();

      const response = await dispatch(handleGetJob(data.id)).unwrap();
      setEditData(response);
      setEditJobDialogMode(DIALOG_MODE_EDIT);
      setOpenEditJobDialog(true);
    } catch (e) {
      handleApiError(e);
    }
  };
  const handleOpenEditCustomer = async () => {
    try {
      await fetchMasterData();

      const response = await dispatch(handleGetDetail(data?.customer?.id ?? '')).unwrap();
      setEditDataCustomer(response);
      setEditCustomerDialogMode(DIALOG_MODE_EDIT);
      setOpenEditCustomerDialog(true);
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleSetOpen = async (open: boolean, needRefreshData?: boolean) => {
    setEditData(undefined);
    setOpenEditJobDialog(open);
    if (needRefreshData) dispatch(jobActions.changeNeedRefreshData(true));
    if (props.updateData) props.updateData();
  };

  const handleSetOpenCustomer = async (open: boolean, needRefreshData?: boolean) => {
    setEditDataCustomer(undefined);
    setOpenEditCustomerDialog(open);
    if (needRefreshData) dispatch(customerActions.changeNeedRefreshData(true));
    if (props.updateData) props.updateData();
  };

  const handleCancel = () => {
    if (props.updateData) props.updateData();
    setIsEditing(false);
  };

  const handleClickHeader = async (item: string, text?: string) => {
    let firstName = text;
    let lastName = '';

    if (item === CUSTOMER_TYPES_CONTACT) {
      const textArray = (text || '').split(' ');
      if (textArray.length === 2) {
        firstName = textArray[0];
        lastName = textArray[1];
      }
    }

    await dispatch(
      handleCreateCustomer({ name: text, firstName, lastName, type: item, isAddressSameParent: true, isCrm: true })
    ).unwrap();
    notify('success', t('createSuccess', { ns: 'admin/common' }));
    await fetchMasterData();
  };

  const handleTopComponent = (text: string) => {
    return (
      <Box className='flex flex-col items-start'>
        {text && (
          <>
            <Button
              onClick={() => handleClickHeader(CUSTOMER_TYPES_CONTACT, text)}
              className={cx('select-click', 'block', 'text-left', 'w-full')}
            >
              {t('addNewContact')}
            </Button>
            <Button
              onClick={() => handleClickHeader(CUSTOMER_TYPES_ORGANIZATION, text)}
              className={cx('select-click', 'block', 'text-left', 'w-full')}
            >
              {t('addNewOrganization')}
            </Button>
          </>
        )}
      </Box>
    );
  };
  return (
    <Box className={cx('basicInfo-container')}>
      <Box component='form' className={cx('detail-container')}>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel> {t('detail.status')}</InputLabel>
            <SelectStatus
              name='job.statusId'
              value={cast(form.values?.job?.statusId)}
              onChange={(value) => form.setFieldValue(`job.statusId`, value)}
              disabled={!isEditing} // Disable field when not editing
            />
          </Box>
          <Box className='w-full'>
            <InputLabel> {t('detail.team')}</InputLabel>
            <SelectAssignee
              options={selectAccounts}
              name='jobAssigneeIds'
              value={data?.assigneeIds}
              onChange={form.handleChange}
              disabled={!isEditing} // Disable field when not editing
            />
          </Box>

          <Box className='w-full'>
            <InputLabel> {t('detail.projectType')}</InputLabel>
            <BaseSelect
              options={jobTypes}
              name='job.jobTypeId'
              label={t('detail.projectType')}
              isSearchEnable={true}
              value={form.values.job.jobTypeId}
              onChange={form.handleChange}
              disabled={!isEditing} // Disable field when not editing
            />
          </Box>
          <Box className='w-full flex items-center'>
            <Box className='mt-[25px] w-[180px]'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.values.job.isAfterHours === true}
                    onChange={(e) => {
                      form.setFieldValue('job.isAfterHours', e.target.checked);
                    }}
                  />
                }
                label={t('properties.isAfterHours')}
                value={VALUE_TRUE}
                disabled={!isEditing}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel> {t('detail.billing')}</InputLabel>
              <BaseSelect
                options={billing}
                name='job.billing'
                label={t('detail.billing')}
                value={form.values.job.billing}
                onChange={form.handleChange}
                disabled={!isEditing} // Disable field when not editing
              />
            </Box>
          </Box>
        </BaseRow>

        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>{t('properties.waterCategory')}</InputLabel>
            <BaseSelect
              name='job.waterCategory'
              placeholder={t('properties.waterCategory')}
              value={cast(form.values.job.waterCategory)}
              options={waterCategory}
              onChange={form.handleChange}
              error={Boolean(form.errors.job?.waterCategory)}
              helperText={form.errors.job?.waterCategory}
              disabled={!isEditing}
            ></BaseSelect>
          </Box>
          <Box className='w-full'>
            <InputLabel>{t('properties.waterClass')}</InputLabel>
            <BaseSelect
              name='job.waterClass'
              placeholder={t('properties.waterClass')}
              value={cast(form.values.job.waterClass)}
              options={waterClass}
              onChange={form.handleChange}
              error={Boolean(form.errors.job?.waterClass)}
              helperText={form.errors.job?.waterClass}
              disabled={!isEditing}
            ></BaseSelect>
          </Box>

          <Box className='w-full'>
            <InputLabel>{t('properties.hazard')}</InputLabel>
            <BaseSelect
              name='job.hazard'
              value={form.values.job.hazard}
              options={hazard}
              onChange={(e) => {
                form.setFieldValue('job.hazard', e.target.value);
              }}
              error={Boolean(form.errors?.job?.hazard)}
              disabled={!isEditing}
              multiple
            />
          </Box>
          <Box className='w-full'>
            <InputLabel>{t('properties.hazardDocumentation')}</InputLabel>
            <TextField
              name='job.hazardDocumentation'
              placeholder={t('properties.hazardDocumentation')}
              value={cast(form.values.job.hazardDocumentation)}
              onChange={form.handleChange}
              error={Boolean(form.errors.job?.hazardDocumentation)}
              helperText={form.errors.job?.hazardDocumentation}
              disabled={!isEditing}
            ></TextField>
          </Box>
        </BaseRow>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>{t('editJobDialog.job.referrer')}</InputLabel>
            <BaseSelect
              name='job.referrer'
              options={allCustomerGroups}
              value={cast(form.values?.job.referrer)}
              onChange={(event) => {
                form.handleChange(event);
              }}
              error={Boolean(form.errors.job?.referrer)}
              helperText={form.errors.job?.referrer}
              isSearchEnable={true}
              disabled={!isEditing}
              topComponent={handleTopComponent}
            />
          </Box>
          <Box className='w-full'>
            <InputLabel>{t('editJobDialog.customer.referralResource')}</InputLabel>
            <BaseSelect
              name='job.referrerSource'
              options={referralSources}
              isHideIconCheckbox={true}
              label={t('editJobDialog.customer.referralResource')}
              value={form.values?.job.referrerSource}
              onChange={(e) => form.setFieldValue('job.referrerSource', e.target.value)}
              error={Boolean(getIn(form.errors.job, 'referrerSource'))}
              helperText={getIn(form.errors.job, 'referrerSource')}
              disabled={!isEditing}
            />
          </Box>
          <Box className='w-full'>
            <InputLabel>{t('properties.accessCode')}</InputLabel>
            <BaseSelect
              name='job.accessCode'
              placeholder={t('properties.accessCode')}
              value={cast(form.values.job.accessCode)}
              options={accessCode}
              onChange={form.handleChange}
              error={Boolean(form.errors.job?.accessCode)}
              helperText={form.errors.job?.accessCode}
              disabled={!isEditing}
            ></BaseSelect>
          </Box>
          {form.values.job.accessCode ? (
            <Box className='w-full'>
              <InputLabel>{t('properties.code')}</InputLabel>
              <TextField
                name='job.code'
                placeholder={t('properties.code')}
                value={cast(form.values?.job.code)}
                onChange={form.handleChange}
                error={Boolean(form.errors.job?.code)}
                helperText={form.errors.job?.code}
                disabled={!isEditing}
              ></TextField>
            </Box>
          ) : (
            <Box className='w-full'></Box>
          )}
        </BaseRow>
        <Box className='mb-[20px]'>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.values.contactInsurance === VALUE_TRUE}
                onChange={(e) => form.setFieldValue('contactInsurance', cast(e.target.checked))}
              />
            }
            label={t('editJobDialog.jobInsurance.insuranceTitle')}
            name='contactInsurance'
            value={VALUE_TRUE}
            disabled={!isEditing}
          />
        </Box>

        {form.values.contactInsurance === VALUE_TRUE && (
          <>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className='w-full'>
                <InputLabel required={true}>{t('editJobDialog.jobInsurance.companyName')}</InputLabel>
                <TextField
                  name='jobInsurance.companyName'
                  placeholder={t('editJobDialog.jobInsurance.companyName')}
                  value={cast(form.values?.jobInsurance?.companyName)}
                  onChange={form.handleChange}
                  error={Boolean(
                    typeof form.errors.jobInsurance === 'string'
                      ? form.errors.jobInsurance
                      : getIn(form.errors, 'jobInsurance.companyName')
                  )}
                  helperText={
                    typeof form.errors.jobInsurance === 'string'
                      ? form.errors.jobInsurance
                      : getIn(form.errors, 'jobInsurance.companyName')
                  }
                  disabled={!isEditing}
                />
              </Box>

              <Box className='w-full'>
                <InputLabel>{t('editJobDialog.jobInsurance.policy')}</InputLabel>
                <TextField
                  name='jobInsurance.policy'
                  placeholder={t('editJobDialog.jobInsurance.policy')}
                  value={cast(form.values?.jobInsurance?.policy)}
                  onChange={form.handleChange}
                  error={Boolean(getIn(form.errors, 'jobInsurance.policy'))}
                  helperText={getIn(form.errors, 'jobInsurance.policy')}
                  disabled={!isEditing}
                />
              </Box>
              <Box className='w-full'>
                <InputLabel>{t('editJobDialog.jobInsurance.claim')}</InputLabel>
                <TextField
                  name='jobInsurance.claim'
                  placeholder={t('editJobDialog.jobInsurance.claim')}
                  value={cast(form.values?.jobInsurance?.claim)}
                  onChange={form.handleChange}
                  error={Boolean(getIn(form.errors, 'jobInsurance.claim'))}
                  helperText={getIn(form.errors, 'jobInsurance.claim')}
                  disabled={!isEditing}
                />
              </Box>
              <Box className='w-full'>
                <InputLabel>{t('editJobDialog.jobInsurance.other')}</InputLabel>
                <TextField
                  name='jobInsurance.other'
                  placeholder={t('editJobDialog.jobInsurance.other')}
                  value={cast(form.values?.jobInsurance?.other)}
                  onChange={form.handleChange}
                  error={Boolean(getIn(form.errors, 'jobInsurance.other'))}
                  helperText={getIn(form.errors, 'jobInsurance.other')}
                  disabled={!isEditing}
                />
              </Box>
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className='w-full'>
                <InputLabel>{t('editJobDialog.jobInsurance.deductible')}</InputLabel>
                <BaseSelect
                  options={option}
                  name='jobInsurance.deductible'
                  label={t('editJobDialog.jobInsurance.deductible')}
                  value={form.values.jobInsurance?.deductible}
                  onChange={form.handleChange}
                  disabled={!isEditing}
                />
              </Box>
              {String(form.values.jobInsurance?.deductible) === 'true' ? (
                <Box className='w-full'>
                  <InputLabel>{t('editJobDialog.jobInsurance.collectedAmount')}</InputLabel>

                  <NumericFormat
                    customInput={TextField}
                    value={cast(form.values?.jobInsurance?.collectedAmount)}
                    onValueChange={(val) => form.setFieldValue(`jobInsurance.collectedAmount`, val?.toString())}
                    thousandSeparator={true}
                    disabled={!isEditing}
                    decimalScale={2}
                  />
                </Box>
              ) : (
                <Box className='w-full'> </Box>
              )}

              <Box className='w-full'> </Box>
              <Box className='w-full'> </Box>
            </BaseRow>
          </>
        )}
        {permissions[PERMISSIONS.project_edit] && (
          <Box className='justify-end flex'>
            {isEditing ? (
              <>
                <Button className='text-[#758CA5] bg-[#F0F7FF] mr-[10px]' onClick={handleCancel}>
                  {t('cancel', { ns: 'admin/common' })}
                </Button>
                <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
                  {t('save', { ns: 'admin/common' })}
                </Button>
              </>
            ) : (
              <>
                <Button color='primary' variant='contained' onClick={handleEdit}>
                  {t('edit', { ns: 'admin/common' })}
                </Button>
              </>
            )}
          </Box>
        )}
        {/* Edit and Save/Cancel Buttons */}
      </Box>

      {/* Client information */}
      <Box className={cx('edit-btn')}>
        <Box className={cx('infomation-container')}>
          <Box>
            <Typography className={cx('header')}> {t('basicInfo.clientinformation')}</Typography>
          </Box>
          <Box className={cx('info')}>
            <Typography variant='body2' className={cx('header-title')}>
              {t('basicInfo.name')}
            </Typography>
            <Typography variant='body2' className={cx('header-title-item')}>
              {data?.customer?.name}
            </Typography>
          </Box>

          <Box className={cx('info')}>
            <Typography variant='body2' className={cx('header-title')}>
              {t('basicInfo.salePerson')}
            </Typography>
            <Typography variant='body2' className={cx('header-title-item')}>
              {data?.customer?.account?.name}
            </Typography>
          </Box>

          <Box className={cx('info')}>
            <Typography variant='body2' className={cx('header-title')}>
              {t('basicInfo.email')}
            </Typography>
            <Typography variant='body2' className={cx('header-title-item')}>
              {data?.customer?.email}
            </Typography>
          </Box>

          <Box className={cx('info')}>
            <Typography variant='body2' className={cx('header-title')}>
              {t('basicInfo.phoneNumber')}
            </Typography>
            <Typography variant='body2' className={cx('header-title-item')}>
              {data?.customer?.phone}
            </Typography>
          </Box>
          <Box className={cx('info')}>
            <Typography variant='body2' className={cx('header-title')}>
              {t('basicInfo.address')}
            </Typography>
            <Typography variant='body2' className={cx('header-title-item')}>
              <LinkAddress address={data?.customer?.fullAddress ?? ''} />
            </Typography>
          </Box>

          <Box className={cx('info')}>
            <Typography variant='body2' className={cx('header-title')}>
              {t('editJobDialog.customer.address1')}
            </Typography>
            <Typography variant='body2' className={cx('header-title-item')}>
              {data?.customer?.address}
            </Typography>
          </Box>

          <Box className={cx('info')}>
            <Typography variant='body2' className={cx('header-title')}>
              {t('editJobDialog.customer.address2')}
            </Typography>
            <Typography variant='body2' className={cx('header-title-item')}>
              {data?.customer?.address2}
            </Typography>
          </Box>
          <Box className={cx('info')}>
            <Typography variant='body2' className={cx('header-title')}>
              {t('editJobDialog.customer.city')}
            </Typography>
            <Typography variant='body2' className={cx('header-title-item')}>
              {data?.customer?.city}
            </Typography>
          </Box>
          <Box className={cx('info')}>
            <Typography variant='body2' className={cx('header-title')}>
              {t('editJobDialog.customer.state')}
            </Typography>
            <Typography variant='body2' className={cx('header-title-item')}>
              {data?.customer?.state}
            </Typography>
          </Box>
          <Box className={cx('info')}>
            <Typography variant='body2' className={cx('header-title')}>
              {t('editJobDialog.customer.zipCode')}
            </Typography>
            <Typography variant='body2' className={cx('header-title-item')}>
              {data?.customer?.zipCode}
            </Typography>
          </Box>
          {/* <Box className={cx('info')}>
            <Typography variant='body2' className={cx('header-title')}>
              {t('editJobDialog.customer.country')}
            </Typography>
            <Typography variant='body2' className={cx('header-title-item')}>
              {data?.customer?.country}
            </Typography>
          </Box> */}
          <Divider sx={{ my: 2 }} />
        </Box>
        <Box className={cx('edit-all-btn')}>
          <Button className=' mr-[10px]' color='primary' variant='contained' onClick={handleOpenEdit}>
            {t('editProject', { ns: 'admin/job' })}
          </Button>
          <Button className=' mr-[10px]' color='primary' variant='contained' onClick={handleOpenEditCustomer}>
            {t('editCustomer', { ns: 'admin/job' })}
          </Button>
        </Box>
      </Box>

      {/* Project Information */}
      <Box className={cx('infomation-container')}>
        <Box>
          <Typography className={cx('header')}>{t('basicInfo.projectInformation')}</Typography>
        </Box>
        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('basicInfo.projectType')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.jobType?.name}
          </Typography>
        </Box>
        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('basicInfo.propertyType')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.jobProperty?.name}
          </Typography>
        </Box>

        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('editJobDialog.job.location')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.location?.name}
          </Typography>
        </Box>
        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('editJobDialog.job.sourceOfLoss')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.sourceOfLoss}
          </Typography>
        </Box>
        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('editJobDialog.job.yearBuilt')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.yearBuilt}
          </Typography>
        </Box>
        {data?.isAddressSameCustomer ? (
          <>
            <Box className={cx('info')}>
              <Typography variant='body2' className={cx('header-title')}>
                {t('editJobDialog.customer.sameasaddressOrganization')}
              </Typography>
              <Typography variant='body2' className={cx('header-title-item')}>
                {t('editJobDialog.customer.yes')}
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <Box className={cx('info')}>
              <Typography variant='body2' className={cx('header-title')}>
                {t('editJobDialog.customer.address')}
              </Typography>
              <Typography variant='body2' className={cx('header-title-item')}>
                <LinkAddress address={data?.fullAddress ?? ''} />
              </Typography>
            </Box>
            <Box className={cx('info')}>
              <Typography variant='body2' className={cx('header-title')}>
                {t('editJobDialog.customer.address1')}
              </Typography>
              <Typography variant='body2' className={cx('header-title-item')}>
                {data?.address}
              </Typography>
            </Box>
            <Box className={cx('info')}>
              <Typography variant='body2' className={cx('header-title')}>
                {t('editJobDialog.customer.address2')}
              </Typography>
              <Typography variant='body2' className={cx('header-title-item')}>
                {data?.address2}
              </Typography>
            </Box>
            <Box className={cx('info')}>
              <Typography variant='body2' className={cx('header-title')}>
                {t('editJobDialog.customer.city')}
              </Typography>
              <Typography variant='body2' className={cx('header-title-item')}>
                {data?.city}
              </Typography>
            </Box>
            <Box className={cx('info')}>
              <Typography variant='body2' className={cx('header-title')}>
                {t('editJobDialog.customer.state')}
              </Typography>
              <Typography variant='body2' className={cx('header-title-item')}>
                {data?.state}
              </Typography>
            </Box>
            <Box className={cx('info')}>
              <Typography variant='body2' className={cx('header-title')}>
                {t('editJobDialog.customer.zipCode')}
              </Typography>
              <Typography variant='body2' className={cx('header-title-item')}>
                {data?.zipCode}
              </Typography>
            </Box>
            {/* <Box className={cx('info')}>
              <Typography variant='body2' className={cx('header-title')}>
                {t('editJobDialog.customer.country')}
              </Typography>
              <Typography variant='body2' className={cx('header-title-item')}>
                {data?.country}
              </Typography>
            </Box> */}
          </>
        )}

        <Divider sx={{ my: 2 }} />
      </Box>
      <EditJobDialog open={openEditJobDialog} setOpen={handleSetOpen} mode={editJobDialogMode} data={editData} />
      <EditCustomerDialog
        open={openEditCustomerDialog}
        setOpen={handleSetOpenCustomer}
        mode={editCustomerDialogMode}
        data={editDataCustomer}
        type={CUSTOMER_TYPES_ORGANIZATION}
      />
    </Box>
  );
};

export default memo(BasicInfo);
